import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { MatchplayMatch, MatchplayTournament, MatchplayPlayer, UpdateHoleRequest } from '../../../types/matchplay-v2';
import { MatchplayDatabase } from '../../../lib/matchplay-v2/database';
import { MatchplayScoring as ScoringUtils } from '../../../lib/matchplay-v2/scoring';
import { SinglesScoring } from './SinglesScoring';
import { FourballScoring } from './FourballScoring';
import { FoursomesScoring } from './FoursomesScoring';

interface MatchplayScoringProps {
  match: MatchplayMatch;
  tournament: MatchplayTournament;
  onBack: () => void;
}

export function MatchplayScoring({ match, tournament, onBack }: MatchplayScoringProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [players, setPlayers] = useState<MatchplayPlayer[]>([]);
  const [currentMatch, setCurrentMatch] = useState<MatchplayMatch | null>(null);
  const [selectedHole, setSelectedHole] = useState<number | null>(null);
  const [lastSubmittedHole, setLastSubmittedHole] = useState<number | null>(null);

  useEffect(() => {
    if (match?.id) {
      loadMatchData();
      setCurrentMatch(match);
    }
  }, [match?.id]);

  const loadMatchData = async () => {
    if (!match?.id || !tournament?.id) return;
    
    try {
      setIsLoading(true);
      const [fetchedPlayers, updatedMatch] = await Promise.all([
        MatchplayDatabase.getPlayers(tournament.id),
        MatchplayDatabase.getMatch(match.id)
      ]);

      setPlayers(fetchedPlayers);
      if (updatedMatch) {
        setCurrentMatch(updatedMatch);
      }
    } catch (error) {
      console.error('Error loading match data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleHoleSubmit = async (holeData: UpdateHoleRequest) => {
    if (!currentMatch?.id) return;
    
    try {
      setIsLoading(true);
      await MatchplayDatabase.updateHole(currentMatch.id, holeData);
      
      // Reload match data to get updated scores
      const updatedMatch = await MatchplayDatabase.getMatch(currentMatch.id);
      if (updatedMatch) {
        setCurrentMatch(updatedMatch);
        // Track the hole that was just submitted
        setLastSubmittedHole(holeData.holeNumber);
        // Clear selected hole after successful submission to allow automatic progression
        setSelectedHole(null);
      }
    } catch (error) {
      console.error('Error submitting hole:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleHoleNavigation = (holeNumber: number) => {
    setSelectedHole(holeNumber);
  };

  const getCurrentHole = () => {
    if (!currentMatch) return 1;
    
    // If a hole has been manually selected, use it
    if (selectedHole !== null) {
      return selectedHole;
    }
    
    // If we have a last submitted hole, continue sequentially from there
    if (lastSubmittedHole !== null) {
      const nextHole = lastSubmittedHole + 1;
      if (nextHole <= 18) {
        return nextHole;
      }
    }
    
    // Otherwise, use the next hole from the scoring system
    return ScoringUtils.getNextHole(currentMatch) || 1;
  };

  const getTeamPlayers = (teamPlayerIds: string[]) => {
    return teamPlayerIds.map(id => players.find(p => p.id === id)).filter(Boolean) as MatchplayPlayer[];
  };

  // Show loading state if match or tournament is not available
  if (!match || !tournament || !currentMatch) {
    return (
      <div className="w-full min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading match...</p>
        </div>
      </div>
    );
  }

  const team1Players = getTeamPlayers(currentMatch.team1Players || []);
  const team2Players = getTeamPlayers(currentMatch.team2Players || []);

  const renderScoringComponent = () => {
    const props = {
      match: currentMatch,
      tournament,
      team1Players,
      team2Players,
      currentHole: getCurrentHole(),
      onHoleSubmit: handleHoleSubmit,
      onHoleNavigation: handleHoleNavigation,
      isLoading
    };

    switch (currentMatch.format) {
      case 'singles':
        return <SinglesScoring {...props} />;
      case 'fourball':
        return <FourballScoring {...props} />;
      case 'foursomes':
        return <FoursomesScoring {...props} />;
      default:
        return (
          <div className="text-center py-8 text-red-500">
            <p>Unsupported match format: {currentMatch.format}</p>
          </div>
        );
    }
  };

  return (
    <div className="w-full min-h-screen bg-gray-50">
      {/* Simple Back Button - Only Header Element */}
      <div className="sticky top-0 z-20 bg-white border-b border-gray-200 shadow-sm">
        <div className="w-full px-4 py-3">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors touch-manipulation"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Tournament</span>
          </button>
        </div>
      </div>

      {/* Full Width Scoring Component */}
      <div className="w-full">
        {renderScoringComponent()}
      </div>
    </div>
  );
} 